import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api } from '../api';
import type { ConfigEntry, ConfigResponse } from '../types';

/**
 * DPE, notice, extrait de règlement : trois fichiers du logement, jamais
 * réellement joignables jusqu'ici (le champ n'était qu'un texte). Le dépôt se
 * fait immédiatement (le fichier part sur le serveur au choix), mais la case
 * dans la configuration ne pointe dessus qu'au clic sur « Enregistrer » du
 * formulaire global, comme tout autre champ.
 */
function PropertyFileField({ entry, value, onChange }: {
  entry: ConfigEntry;
  value: string;
  onChange: (next: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function upload(file: File): Promise<void> {
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const result = await api<{ id: string }>(`/api/admin/property-documents?role=${entry.fileRole}`, {
        method: 'POST', body: form,
      });
      onChange(result.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Envoi impossible.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="property-file-field">
      {value
        ? <a href={`/api/files/${value}`} target="_blank" rel="noreferrer">Voir le fichier déposé</a>
        : <p className="muted">Aucun fichier déposé.</p>}
      <label className="text-button">
        {uploading ? 'Envoi…' : value ? 'Remplacer' : 'Déposer un fichier'}
        <input
          type="file"
          accept="application/pdf,image/jpeg,image/png,.docx"
          hidden
          disabled={uploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
            event.target.value = '';
          }}
        />
      </label>
      {error && <p className="warn-text">{error}</p>}
    </div>
  );
}

/**
 * La signature du bailleur ne se saisit pas comme un champ texte : sans
 * aperçu, on ne sait jamais si celle enregistrée est encore la bonne, et sans
 * canevas, impossible d'en tracer une nouvelle. `onChange` alimente le même
 * brouillon que les autres champs : le tracé n'est écrit qu'au clic sur
 * « Enregistrer » du formulaire global, pas à part.
 */
function SignatureImageField({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, rect.width, rect.height);
    context.strokeStyle = '#17201f';
    context.lineWidth = 2.25;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    hasInk.current = false;
  }, [editing]);

  function point(event: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>): void {
    const context = event.currentTarget.getContext('2d');
    if (!context) return;
    drawing.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    const p = point(event);
    context.beginPath();
    context.moveTo(p.x, p.y);
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (!drawing.current) return;
    const context = event.currentTarget.getContext('2d');
    if (!context) return;
    const p = point(event);
    context.lineTo(p.x, p.y);
    context.stroke();
    hasInk.current = true;
  }

  function stop(): void { drawing.current = false; }

  function clear(): void {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.restore();
    hasInk.current = false;
  }

  if (!editing) {
    return (
      <div className="signature-field">
        {value
          ? <img src={value} alt="Signature du bailleur enregistrée" className="signature-preview" />
          : <p className="muted">Aucune signature enregistrée.</p>}
        <div className="signature-actions">
          <button type="button" className="text-button" onClick={() => setEditing(true)}>
            {value ? 'Changer la signature' : 'Tracer une signature'}
          </button>
          {value && <button type="button" className="text-button" onClick={() => onChange('')}>Supprimer</button>}
        </div>
      </div>
    );
  }
  return (
    <div className="signature-field">
      <canvas
        ref={canvasRef}
        className="signature-canvas"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerCancel={stop}
        aria-label="Zone de signature du bailleur"
      />
      <div className="signature-actions">
        <button type="button" className="text-button" onClick={clear}>Effacer</button>
        <button
          type="button"
          className="button compact"
          onClick={() => {
            if (!hasInk.current) return;
            onChange(canvasRef.current!.toDataURL('image/png'));
            setEditing(false);
          }}
        >
          Utiliser ce tracé
        </button>
        <button type="button" className="text-button" onClick={() => setEditing(false)}>Annuler</button>
      </div>
    </div>
  );
}

function InputForEntry({ entry, value, onChange }: {
  entry: ConfigEntry;
  value: string;
  onChange: (next: string) => void;
}) {
  if (entry.kind === 'multiline') {
    return <textarea rows={4} value={value} onChange={(event) => onChange(event.target.value)} />;
  }
  if (entry.kind === 'boolean') {
    return (
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option><option value="OUI">Oui</option><option value="NON">Non</option>
      </select>
    );
  }
  if (entry.kind === 'enum') {
    return (
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option>
        {entry.options?.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    );
  }
  if (entry.kind === 'date') {
    return <input type="date" value={value} onChange={(event) => onChange(event.target.value)} />;
  }
  if (entry.kind === 'number' || entry.kind === 'money') {
    return <input inputMode="decimal" value={value} onChange={(event) => onChange(event.target.value)} placeholder={entry.kind === 'money' ? '€' : ''} />;
  }
  if (entry.kind === 'image') {
    return <SignatureImageField value={value} onChange={onChange} />;
  }
  if (entry.kind === 'file') {
    return <PropertyFileField entry={entry} value={value} onChange={onChange} />;
  }
  return <input value={value} onChange={(event) => onChange(event.target.value)} />;
}

export function ConfigPanel() {
  const [data, setData] = useState<ConfigResponse | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    try {
      const result = await api<ConfigResponse>('/api/admin/config');
      setData(result);
      setDraft({});
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, []);

  const groups = useMemo(() => {
    const map = new Map<string, ConfigEntry[]>();
    for (const entry of data?.entries ?? []) {
      const list = map.get(entry.group) ?? [];
      list.push(entry);
      map.set(entry.group, list);
    }
    return [...map.entries()];
  }, [data]);

  const changedCount = Object.keys(draft).length;

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (changedCount === 0) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await api<ConfigResponse & { updated: number }>('/api/admin/config', {
        method: 'PATCH',
        body: JSON.stringify({ changes: draft }),
      });
      setData({ entries: result.entries, missingRequired: result.missingRequired });
      setDraft({});
      setNotice(`${result.updated} valeur(s) enregistrée(s).`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return <section className="panel">{error ? <div className="inline-message error">{error}</div> : <p className="muted">Chargement…</p>}</section>;
  }

  return (
    <form className="panel" onSubmit={(event) => void save(event)}>
      <div className="section-heading">
        <div><p className="eyebrow">Paramètres</p><h2>Configuration du logement</h2></div>
        <button className="button primary compact" disabled={busy || changedCount === 0}>
          {busy ? 'Enregistrement…' : changedCount === 0 ? 'Aucune modification' : `Enregistrer (${changedCount})`}
        </button>
      </div>
      <p className="section-intro">
        Ces valeurs alimentent les documents produits. Une modification ne change jamais un document déjà généré.
      </p>

      {data.missingRequired.length > 0 && (
        <div className="inline-message error" role="alert">
          {data.missingRequired.length} valeur(s) indispensable(s) au bail manquent :{' '}
          {data.missingRequired.map((key) => data.entries.find((entry) => entry.key === key)?.label ?? key).join(', ')}.
        </div>
      )}
      {notice && <div className="inline-message success" role="status">{notice}</div>}
      {error && <div className="inline-message error" role="alert">{error}</div>}

      {groups.map(([group, entries]) => (
        <fieldset className="config-group" key={group}>
          <legend>{group}</legend>
          <div className="form-grid">
            {entries.map((entry) => (
              <label key={entry.key} className={entry.kind === 'multiline' ? 'span-2' : ''}>
                {entry.label}{entry.required && <span className="required-mark" title="Indispensable au bail"> *</span>}
                <InputForEntry
                  entry={entry}
                  value={draft[entry.key] ?? entry.value}
                  onChange={(next) => setDraft((current) => ({ ...current, [entry.key]: next }))}
                />
                {entry.help && <small className="field-help">{entry.help}</small>}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </form>
  );
}
